import type {
  BinaryOperatorNode as BinaryOperatorNodeType,
  Port,
  VariableValueType,
} from "../../../../util/src/blueprint/types.js";
import { dateDifferencePorts } from "../../../../util/src/blueprint/date-operators.js";
import { getBehaviorObjectFieldOptions } from "../../../../util/src/blueprint/object-path-typing.js";
import { useBehaviorPathSchema } from "../BehaviorPathSchemaContext.js";
import { OperatorNodeCard } from "./OperatorNodeCard.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import React, { useCallback } from "react";

export interface BinaryOperatorNodeProps {
  node: BinaryOperatorNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
  onListItemValueTypeChange?: (
    nodeId: string,
    valueType: Exclude<VariableValueType, "list">,
  ) => void;
  onObjectFieldPathChange?: (nodeId: string, fieldPath: string) => void;
}

const BinaryOperatorNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
  onListItemValueTypeChange,
  onObjectFieldPathChange,
}: BinaryOperatorNodeProps) => {
  const bpDico = dico().behavior.operators;
  const datePorts =
    node.operator === "dateDifference"
      ? dateDifferencePorts(bpDico.dateDifferencePorts)
      : [];
  const displayedPorts =
    node.operator === "dateDifference"
      ? node.ports.map((port) => ({
          ...port,
          label:
            datePorts.find((item) => item.id === port.id)?.label ?? port.label,
        }))
      : node.ports;
  const inputPorts = displayedPorts.filter((p) => p.direction === "input");

  const pathSchema = useBehaviorPathSchema(node.objectSource);
  const objectFieldOptions =
    node.operator === "find" &&
    node.objectSource &&
    node.listItemValueType === "object"
      ? getBehaviorObjectFieldOptions(
          pathSchema,
          node.objectFieldPathBase,
          node.objectFieldPath,
        )
      : [];
  const showFindFieldSelect =
    node.operator === "find" &&
    !!onObjectFieldPathChange &&
    objectFieldOptions.length > 0;

  const handleFindFieldChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      onObjectFieldPathChange?.(node.id, event.target.value);
    },
    [node.id, onObjectFieldPathChange],
  );

  return (
    <OperatorNodeCard>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-evenly",
          height: "100%",
          gap: 4,
        }}
      >
        {inputPorts.map((port, index) => {
          const portElement = (
            <PortHandle
              setPortsMap={setPortsMap}
              node={node}
              key={port.id}
              port={port}
              showLabel={node.operator === "dateDifference"}
              onMouseDown={() => onPortMouseDown?.(node.id, port)}
              onMouseUp={() => onPortMouseUp?.(node.id, port)}
              showValueInput={port.valueType !== "list"}
              isConnected={isPortConnected?.(node.id, port.id) ?? false}
              onValueChange={(value) =>
                onPortValueChange?.(node.id, port.id, value)
              }
              onListItemValueTypeChange={onListItemValueTypeChange}
            />
          );

          const insertFindSelectAfter =
            showFindFieldSelect && index === 0 && port.id === "input1";

          if (!insertFindSelectAfter) {
            return portElement;
          }

          return (
            <React.Fragment key={port.id}>
              {portElement}
              <FormControl
                size="small"
                variant="outlined"
                onMouseDown={(event) => event.stopPropagation()}
                sx={{
                  minWidth: 132,
                  alignSelf: "flex-start",
                  ml: 0.5,
                  "& .MuiInputBase-input": {
                    py: 0.45,
                    px: 0.75,
                    fontSize: 12,
                  },
                }}
              >
                <Select<string>
                  displayEmpty
                  value={
                    node.objectFieldPath &&
                    objectFieldOptions.some(
                      (option) => option.value === node.objectFieldPath,
                    )
                      ? node.objectFieldPath
                      : (objectFieldOptions[0]?.value ?? "")
                  }
                  onChange={handleFindFieldChange}
                >
                  {objectFieldOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </React.Fragment>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          gap: 6,
        }}
      >
        <span
          style={{
            fontSize: 30,
            fontWeight: 600,
            textTransform: "uppercase",
            color: "#333333",
            padding: "0 30px",
          }}
        >
          {bpDico[node.operator]}
        </span>
      </div>
      <div
        style={
          node.operator === "dateDifference"
            ? { display: "flex", flexDirection: "column", gap: 4 }
            : undefined
        }
      >
        {displayedPorts
          .filter((p) => p.direction === "output")
          .map((port) => (
            <PortHandle
              setPortsMap={setPortsMap}
              node={node}
              key={port.id}
              port={port}
              showLabel={node.operator === "dateDifference"}
              onMouseDown={() => onPortMouseDown?.(node.id, port)}
              onMouseUp={() => onPortMouseUp?.(node.id, port)}
              isConnected={isPortConnected?.(node.id, port.id) ?? false}
              onValueChange={(value) =>
                onPortValueChange?.(node.id, port.id, value)
              }
              onListItemValueTypeChange={onListItemValueTypeChange}
            />
          ))}
      </div>
    </OperatorNodeCard>
  );
};

export const BinaryOperatorNode = React.memo(BinaryOperatorNodeComponent);
